import { useEffect, useRef, useState, useCallback } from 'react';
import { 
    animateElement, 
    fadeIn, 
    slideUp, 
    slideDown, 
    scaleIn, 
    bounce,
    createScrollAnimations,
    respectsReducedMotion,
    safeAnimate,
    ANIMATION_PRESETS,
    KEYFRAMES
} from '../utils/animations';

export type AnimationType = 'fadeIn' | 'fadeInUp' | 'slideUp' | 'slideDown' | 'scaleIn' | 'bounce' | 'custom';

interface UseAnimationOptions {
    type: AnimationType;
    duration?: number;
    delay?: number;
    trigger?: 'mount' | 'scroll' | 'hover' | 'click' | 'manual';
    stagger?: boolean;
    staggerDelay?: number;
    customKeyframes?: Record<string, any>;
    customConfig?: {
        duration: number;
        easing: string;
        delay?: number;
        fillMode?: 'forwards' | 'backwards' | 'both' | 'none';
    };
}

export const useAnimation = (options: UseAnimationOptions) => {
    const elementRef = useRef<HTMLElement>(null);
    const [isAnimating, setIsAnimating] = useState(false);
    const [hasAnimated, setHasAnimated] = useState(false);
    const animationRef = useRef<Animation | null>(null);

    const animate = useCallback(() => {
        if (!elementRef.current || hasAnimated) return;

        setIsAnimating(true);
        setHasAnimated(true);

        const element = elementRef.current;
        let animation: Animation | null = null;

        if (options.type === 'custom' && options.customKeyframes && options.customConfig) {
            animation = safeAnimate(element, options.customKeyframes, options.customConfig);
        } else {
            switch (options.type) {
                case 'fadeIn':
                    animation = fadeIn(element, options.duration);
                    break;
                case 'fadeInUp':
                    animation = animateElement(element, KEYFRAMES.fadeInUp, {
                        ...ANIMATION_PRESETS.fadeIn,
                        duration: options.duration || 300
                    });
                    break;
                case 'slideUp':
                    animation = slideUp(element, options.duration);
                    break;
                case 'slideDown':
                    animation = slideDown(element, options.duration);
                    break;
                case 'scaleIn':
                    animation = scaleIn(element, options.duration);
                    break;
                case 'bounce':
                    animation = bounce(element, options.duration);
                    break;
            }
        }

        if (animation) {
            animationRef.current = animation;
            
            animation.addEventListener('finish', () => {
                setIsAnimating(false);
            });
        } else {
            setIsAnimating(false);
        }
    }, [options.type, options.duration, options.customKeyframes, options.customConfig, hasAnimated]);

    const reset = useCallback(() => {
        if (animationRef.current) {
            animationRef.current.cancel();
        }
        setHasAnimated(false);
        setIsAnimating(false);
    }, []);

    const stop = useCallback(() => {
        if (animationRef.current) {
            animationRef.current.cancel();
        }
        setIsAnimating(false);
    }, []);

    useEffect(() => {
        if (options.trigger === 'mount') {
            const timer = setTimeout(animate, options.delay || 0);
            return () => clearTimeout(timer);
        }
    }, [animate, options.trigger, options.delay]);

    useEffect(() => {
        if (options.trigger === 'scroll' && elementRef.current) {
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting && !hasAnimated) {
                            animate();
                        }
                    });
                },
                {
                    threshold: 0.1,
                    rootMargin: '0px 0px -50px 0px'
                }
            );

            observer.observe(elementRef.current);

            return () => {
                observer.disconnect();
            };
        }
    }, [animate, options.trigger, hasAnimated]);

    return {
        elementRef,
        animate,
        reset,
        stop,
        isAnimating,
        hasAnimated
    };
};

export const useStaggerAnimation = (
    count: number,
    options: Omit<UseAnimationOptions, 'stagger' | 'staggerDelay'> & { staggerDelay?: number }
) => {
    const [elements, setElements] = useState<HTMLElement[]>([]);
    const [isAnimating, setIsAnimating] = useState(false);
    const animationRefs = useRef<Animation[]>([]);

    const addElement = useCallback((element: HTMLElement | null) => {
        if (element && !elements.includes(element)) {
            setElements(prev => [...prev, element]);
        }
    }, [elements]);

    const animateAll = useCallback(async () => {
        if (elements.length === 0) return;

        setIsAnimating(true);

        const animations: Animation[] = [];

        elements.forEach((element, index) => {
            const delay = index * (options.staggerDelay || 100);
            
            setTimeout(() => {
                let animation: Animation | null = null;

                switch (options.type) {
                    case 'fadeIn':
                        animation = fadeIn(element, options.duration);
                        break;
                    case 'slideUp':
                        animation = slideUp(element, options.duration);
                        break;
                    case 'slideDown':
                        animation = slideDown(element, options.duration);
                        break;
                    case 'scaleIn':
                        animation = scaleIn(element, options.duration);
                        break;
                    case 'bounce':
                        animation = bounce(element, options.duration);
                        break;
                }

                if (animation) {
                    animations.push(animation);
                    animationRefs.current.push(animation);
                }
            }, delay);
        });

        // Wait for all animations to complete
        await Promise.all(animations.map(anim => 
            new Promise(resolve => {
                anim.addEventListener('finish', resolve);
            })
        ));

        setIsAnimating(false);
    }, [elements, options]);

    const reset = useCallback(() => {
        animationRefs.current.forEach(animation => animation.cancel());
        animationRefs.current = [];
        setIsAnimating(false);
    }, []);

    return {
        addElement,
        animateAll,
        reset,
        isAnimating
    };
};

export const useScrollAnimations = () => {
    const observerRef = useRef<IntersectionObserver | null>(null);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        observerRef.current = createScrollAnimations();

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    const observeElement = useCallback((element: HTMLElement, animationType: string = 'fadeIn') => {
        if (observerRef.current) {
            element.dataset.animation = animationType;
            observerRef.current.observe(element);
        }
    }, []);

    const unobserveElement = useCallback((element: HTMLElement) => {
        if (observerRef.current) {
            observerRef.current.unobserve(element);
        }
    }, []);

    return {
        observeElement,
        unobserveElement
    };
};

export const usePageTransition = () => {
    // const [isTransitioning, setIsTransitioning] = useState(false);

    const enter = useCallback((element: HTMLElement) => {
        if (respectsReducedMotion()) {
            element.style.opacity = '1';
            element.style.transform = 'translateY(0)';
            return;
        }

        element.style.opacity = '0';
        element.style.transform = 'translateY(20px)';
        
        requestAnimationFrame(() => {
            element.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
            element.style.opacity = '1';
            element.style.transform = 'translateY(0)';
        });
    }, []);

    const exit = useCallback((element: HTMLElement) => {
        if (respectsReducedMotion()) {
            element.style.opacity = '0';
            element.style.transform = 'translateY(-20px)';
            return;
        }

        element.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        element.style.opacity = '0';
        element.style.transform = 'translateY(-20px)';
    }, []);

    return {
        enter,
        exit
        // isTransitioning
    };
};

const animationHooks = {
    useAnimation,
    useStaggerAnimation,
    useScrollAnimations,
    usePageTransition
};

export default animationHooks;
